import _ from 'lodash';
import * as d3 from 'd3-array';
import { UNKNOWN_DATUM } from 'business/constants';
import { isContinuous, isDateOrTime } from 'helpers/chartHelper';
import { FormatedVizData } from './types';

const isDictDataValue = (
    datum: ResponseDataValueT,
): datum is DictAllDataValueT =>
    // Check if datum is DictAllDataValueT in order to parse it correctly.
    typeof datum === 'object' &&
    datum !== null &&
    'label' in datum &&
    'value' in datum;

/**
 * Group data into bins to create an histogram.
 * If we have a zKey we have to make a second loop to ensure we don't mix the series.
 * Otherwise we simply used the grouped data, and we apply the correct aggregation.
 * TODO: Fix this !
 * We build histograms based on the value of averaged values, and we doesn't take into
 * account the raw data, which can lead to serious discrepancies.
 * For example a series that would look like this :
 * 1  |        2        |     3      |  4 |
 * 10 | 20, 20, 20, 20  | 30, 30, 30 | 40 |
 * If we ask for the average, the query response will be
 * 1  | 2  | 3  | 4
 * 10 | 20 | 30 | 40
 * And we will group the histogram in two buckets, obtaining
 * 1-2 | 3-4 |
 * 15  |  35
 * which is completely wrong !
 * The real average of the bucket 1-2 is 18, and for 3-4 it's 32.5.
 */
function formatDataToHistogram(
    data: ResponseDataT[],
    aggregation: string,
    orderedColumns: {
        y: ResponseColumnT;
        x: ResponseColumnT;
        z?: ResponseColumnT;
    },
    isPyramid: boolean = false,
): Record<string, number | string>[] {
    // TODO: improve typing
    const { x, y, z } = orderedColumns;
    const xKey = x.key;
    const yKey = y.key;

    const pyramidDomain: [number, number] = [15, 100];
    const pyramidThresholds = (pyramidDomain[1] - pyramidDomain[0]) / 5;
    const histogram = isPyramid
        ? d3
              .bin<ResponseDataT, number>()
              .domain(pyramidDomain)
              .thresholds(pyramidThresholds)
        : d3.bin<ResponseDataT, number>();

    const histogramData = histogram.value((d) => d[xKey] as number)(
        data.filter((d) => d[xKey] !== UNKNOWN_DATUM),
    );
    // The first interval is not reliable, see https://github.com/d3/d3-array/issues/46#issuecomment-269873644
    const interval = histogramData[1]!.x1! - histogramData[1]!.x0!;

    histogramData[0].x0 = histogramData[0]!.x1! - interval;
    histogramData[histogramData.length - 1].x1 =
        histogramData[histogramData.length - 1]!.x0! + interval;

    const response: Record<string, number | string>[] = [];
    histogramData.forEach((bin) => {
        const xValue = (bin.x1! + bin.x0!) / 2;
        if (z) {
            const zKey = z.key;
            const groupedBin = _.groupBy(bin, (d) => {
                const datum = d[zKey];
                return isDictDataValue(datum) ? datum.label : datum;
            });
            Object.entries(groupedBin).forEach(([zValue, d]) => {
                const yValue =
                    aggregation === 'average'
                        ? _.meanBy(d, yKey)
                        : _.sumBy(d, yKey);
                response.push({
                    [xKey]: xValue,
                    [yKey]: yValue,
                    [zKey]: zValue,
                });
            });
        } else {
            const yValue =
                aggregation === 'average'
                    ? _.meanBy(bin, yKey)
                    : _.sumBy(bin, yKey);
            response.push({ [xKey]: xValue, [yKey]: yValue });
        }
    });
    return response;
}

export const formatDataToHistogramWithContext = (
    context: FormatedVizData,
): FormatedVizData => {
    const {
        chartData,
        chartColumns,
        context: { vizType, queryMetrics },
    } = context;
    // We create histogram data if that is required. For now it only happens for bar charts,
    // or when the xColumn is an hour.
    // We might expand this feature to other graphs with a setting later.
    // We can also add a setting allowing the user to modify the size of the bin
    if (chartColumns.x) {
        const xType = chartColumns.x.type;
        const isHistogram =
            ['bar', 'horizontal_bar', 'pyramid', 'polar_bar'].includes(
                vizType,
            ) &&
            isContinuous(xType) &&
            !isDateOrTime(xType);
        const isPyramid = vizType === 'pyramid';
        if (isHistogram) {
            // We only take the first aggregation, since they are all the same at the moment.
            const aggregation = queryMetrics?.[0]?.aggregation;
            const newChartData = formatDataToHistogram(
                chartData,
                aggregation,
                // @ts-expect-error typescript does not detect that x is defined here
                chartColumns,
                isPyramid,
            );
            return {
                ...context,
                chartData: newChartData,
            };
        }
    }
    return context;
};
